import { ColumnConfig } from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import SmsOptInQuickFilter from '@Components/CustomDataTableFilters/SmsOptInQuickFilter';
import { ResourceTableView } from '@Components/ResourceTable';
import { useResourceTableController } from '@Components/ResourceTable/useResourceTableController';
import type { Fish } from '@Types/fish';
import { buildFilterParams } from '@Utils/buildFilterParams';
import { formatPhoneNumber } from '@Utils/utils';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router-dom';
import { useMembers } from '../../hooks/useMembers';

const NOT_AVAILABLE = 'N/A';

const getBestEffortProfileValue = (fish: any, property: string) => {
  const profile = fish?.memberProfile || fish?.fishProfiles?.[0];
  if (!profile) return NOT_AVAILABLE;
  return profile[property] ?? NOT_AVAILABLE;
};

// ---------- column configs ----------

const fishColumnConfigs: ColumnConfig<Fish>[] = [
  {
    id: 'fName',
    headerLabel: 'First Name',
    accessor: (row) => getBestEffortProfileValue(row, 'fName'),
    type: 'text',
    size: 120,
    enableSorting: false,
  },
  {
    id: 'lName',
    headerLabel: 'Last Name',
    accessor: (row) => getBestEffortProfileValue(row, 'lName'),
    type: 'text',
    size: 120,
    enableSorting: false,
  },
  {
    id: 'email',
    headerLabel: 'Email',
    accessor: (row) => getBestEffortProfileValue(row, 'email'),
    type: 'text',
    enableSorting: false,
  },
  {
    id: 'phoneNumber',
    headerLabel: 'Phone Number',
    accessor: (row) => row.phoneNumber ?? '',
    type: 'text',
    cell: (info) => {
      const value = info.getValue();
      return value ? formatPhoneNumber(value) : 'N/A';
    },
  },
  {
    id: 'smsOptIn',
    headerLabel: 'SMS Opt-In',
    accessor: (row) => (row.smsOptIn ? 'Opted in' : 'Not opted in'),
    type: 'text',
    enableFiltering: false,
    enableSorting: false,
  },
  {
    id: 'createdAt',
    headerLabel: 'Created At',
    accessor: (row) => row.createdAt ?? '',
    type: 'text',
    cell: (info) => {
      const date = info.getValue();
      if (!date) return 'N/A';
      const dt = DateTime.fromISO(date);
      const dateString = dt.toLocaleString(DateTime.DATETIME_MED);
      return dateString || 'N/A';
    },
  },
];

const FISH_FIELD_MAPPING: Record<string, string> = {
  fName: 'filter[fishProfiles][some][fName]',
  lName: 'filter[fishProfiles][some][lName]',
  email: 'filter[fishProfiles][some][email]',
  phoneNumber: 'filter[phoneNumber]',
};

const FISH_STATIC_PARAMS: Record<string, string> = {
  'include[subscriptions][filter][cancelledAt]': 'null',
  'include[subscriptions][filter][deletedAt]': 'null',
  'include[fishProfiles][filter][deletedAt]': 'null',
};

export default function FishList() {
  const navigate = useNavigate();

  const controller = useResourceTableController<Fish>({
    columnsConfig: fishColumnConfigs,
    defaultSort: {
      id: 'createdAt',
      desc: true,
    },
    baseSearchParams: () => ({
      include: [
        'subscriptions.subscriptionTier',
        'sharedSubscription.subscription.subscriptionTier',
        'fishProfiles',
        'wallet',
      ].join(','),
    }),
    buildFilterParams: (filters) =>
      buildFilterParams(filters, {
        fieldMapping: FISH_FIELD_MAPPING,
        staticParams: FISH_STATIC_PARAMS,
      }),
    quickFilterFields: [
      { field: 'fName', label: 'First Name' },
      { field: 'lName', label: 'Last Name' },
      { field: 'email', label: 'Email' },
      { field: 'phoneNumber', label: 'Phone Number' },
    ],
    initialPageSize: 10,
    initialPinning: {
      left: ['select', 'fName', 'lName'],
      right: [],
    },
  });

  const { data: payload, status } = useMembers({
    searchParams: controller.searchParams,
  });

  const rows: Fish[] = Array.isArray(payload) ? payload : (payload?.data ?? []);

  const meta = Array.isArray(payload) ? undefined : payload?.meta;

  return (
    <ResourceTableView<Fish>
      label="Guests"
      controller={controller}
      data={rows}
      meta={meta}
      status={status as any}
      onRowClick={(row) => navigate(`/guests/${row.id}`)}
      renderQuickFiltersExtra={({ table }) => (
        <SmsOptInQuickFilter<Fish> table={table} />
      )}
    />
  );
}
